import { createContext, useContext } from 'react';

/**
 * The shell's toast stack (snackbar.css, `.toast-stack`): one fixed column at
 * the bottom of the screen that holds, top to bottom, the update prompt, the
 * send's "Sending… · Undo" and the mail view's "Message archived · Undo".
 *
 * # Why one column
 *
 * Each of those used to position itself: the same bottom offset, the same
 * z-index, and full width on a phone. Two of them on screen at once therefore
 * sat exactly on top of each other, and whichever came later in the document
 * won the hit test. The update prompt came last, so a thumb aimed at a send's
 * Undo pressed the prompt instead and the message went out. Stepping one up by
 * a fixed height (what the send/mail pair did) breaks as soon as a sentence
 * wraps to two lines. In one flex column they cannot overlap whatever their
 * heights: each takes its own row.
 *
 * # Why a portal for the mail snackbar
 *
 * The send snackbar and the prompt are rendered by the shell, so they are in
 * the column already. The mail snackbar belongs to `MailView` -- mounting it
 * is what makes archive/delete undoable -- and lives in a different subtree.
 * The shell publishes a slot element here; `UndoSnackbar` portals into it when
 * there is one and renders in place (fixed, as before) when there is not: the
 * popped-out message window and component tests have no shell.
 */
export const ToastSlotContext = createContext<HTMLElement | null>(null);

export function useToastSlot(): HTMLElement | null {
  return useContext(ToastSlotContext);
}
